import { formatPropertyAddress } from '../utils/propertyAddress';
import './MobileMapDisclosure.css';
import MapPlaceLayers from './MapPlaceLayers';
import {addIllustratedBasemap} from '../services/illustratedBasemap';
import { propertyFetch } from '../services/idxPropertyClient';
import { loadPropertyMedia, propertyMediaUrls } from '../services/propertyMedia';
import { rememberPropertyPhoto } from '../services/propertyThumbnails';
import { sendEmailRequest } from '../services/emailDeliveryService';
import { usePropertySave } from './PropertySaveProvider';
import RobustSearchControls from './RobustSearchControls';
import useListSearchFilters from '../utils/useListSearchFilters';
import { searchFromQuery, searchQuery } from '../utils/propertySearchFilters';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { SITE_CONFIG } from '../config';
import PropertyThumbnail from './PropertyThumbnail';
import { decryptPassword } from '../utils/decrypt';
import { warmPropertyBatch } from '../utils/warmPropertyDetail';
import PropertyDetailModal from './PropertyDetailModal';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './PropertyListView.css';

const PropertyListView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const initialListFilters = location.state?.searchFilters || searchFromQuery(location.search);

  const [properties, setProperties] = useState(() => location.state?.properties || []);
  const [initialProperties, setInitialProperties] = useState([]);
  const [listViewPage, setListViewPage] = useState(0);
  const [propertyDetail, setPropertyDetail] = useState(null);
  const [propertyDetailImages, setPropertyDetailImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const detailRequestRef = useRef(0);
  const [likedProperties, setLikedProperties] = useState(new Set());
  const { favorites, toggleFavorite } = usePropertySave();
  useEffect(() => { setLikedProperties?.(favorites); }, [favorites]);
  const [expandedMap, setExpandedMap] = useState(null);
  const [placesExpandedMapInstance,setPlacesExpandedMapInstance]=useState(null);
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const [isAdmin] = useState(() => {
    try { const u = JSON.parse(localStorage.getItem('user')); return u?.roles?.includes('ROLE_ADMIN') || u?.roles?.includes('ROLE_ASSISTANT') || false; } catch { return false; }
  });
  const [selectedListings, setSelectedListings] = useState(new Set());
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [bulkEmailRecipient, setBulkEmailRecipient] = useState('');
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailCustomerName, setBulkEmailCustomerName] = useState('');
  const [bulkEmailPreview, setBulkEmailPreview] = useState('');
  const [bulkEmailGenerating, setBulkEmailGenerating] = useState(false);
  const [bulkEmailSending, setBulkEmailSending] = useState(false);
  const [senderEmail, setSenderEmail] = useState('');
  const [senderPassword, setSenderPassword] = useState('');
  const [quickFilters, setQuickFilters] = useState({
    city: String(initialListFilters.city || ''),
    minPrice: initialListFilters.minPrice ?? '',
    maxPrice: initialListFilters.maxPrice ?? '',
    minBedrooms: initialListFilters.minBedrooms ?? ''
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [, setFiltersLoading] = useState(false);
  const [, setFiltersError] = useState('');
  const filterRequestRef = useRef(0);

  const listSearch = useListSearchFilters({
    initialFilters: location.state?.searchFilters || searchFromQuery(location.search),
    initialProperties: location.state?.properties,
    quickEdits: quickFilters,
    defaultState: SITE_CONFIG.SEARCH_STATES || SITE_CONFIG.STATE,
    siteId: SITE_CONFIG.SITE_ID, onResults: setProperties, onPageReset: setListViewPage, pageSize:9
  });
  const filtersLoading = listSearch.loading;
  const filtersError = listSearch.error;
  const isFilterActive = Boolean(
    quickFilters.city.trim() ||
    quickFilters.minPrice ||
    quickFilters.maxPrice ||
    quickFilters.minBedrooms
  );

  const activeFilterCount = [
    quickFilters.city.trim(),
    quickFilters.minPrice,
    quickFilters.maxPrice,
    quickFilters.minBedrooms
  ].filter(Boolean).length;

  const formatCompactPrice = (value) => {
    const amount = Number(value);
    if (amount >= 1000000) return `${amount / 1000000}M`;
    return `${amount / 1000}k`;
  };

  const activeFilterChips = [
    quickFilters.city.trim() && { key: 'city', label: quickFilters.city.trim() },
    quickFilters.minPrice && { key: 'minPrice', label: `${formatCompactPrice(quickFilters.minPrice)}+` },
    quickFilters.maxPrice && { key: 'maxPrice', label: `Up to ${formatCompactPrice(quickFilters.maxPrice)}` },
    quickFilters.minBedrooms && { key: 'minBedrooms', label: `${quickFilters.minBedrooms}+ beds` }
  ].filter(Boolean);

  const priceOptions = [
    { value: '', label: 'Any' },
    { value: '100000', label: '$100k' },
    { value: '200000', label: '$200k' },
    { value: '300000', label: '$300k' },
    { value: '400000', label: '$400k' },
    { value: '500000', label: '$500k' },
    { value: '750000', label: '$750k' },
    { value: '1000000', label: '$1M' }
  ];

  const bedroomOptions = [
    { value: '', label: 'Any' },
    { value: '1', label: '1+' },
    { value: '2', label: '2+' },
    { value: '3', label: '3+' },
    { value: '4', label: '4+' },
    { value: '5', label: '5+' }
  ];

  const updateQuickFilter = (key, value) => {
    setQuickFilters(prev => ({ ...prev, [key]: value }));
    setListViewPage(0);
  };

  const clearQuickFilters = () => {
    setQuickFilters({ city: '', minPrice: '', maxPrice: '', minBedrooms: '' });
    listSearch.setFilters(previous => ({...previous, city:undefined, minPrice:undefined, maxPrice:undefined, minBedrooms:undefined}));
    setFiltersError('');
    setFiltersLoading(false);
    setListViewPage(0);

  };

  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const res = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?siteId=${SITE_CONFIG.SITE_ID}`, { headers: { 'X-API-Key': 'treas-api-key-2025' } });
        if (res.ok) {
          const data = await res.json();
          if (data.senderEmail) setSenderEmail(data.senderEmail);
          if (data.senderEmailPassword) decryptPassword(data.senderEmailPassword).then(pw => { if (pw) setSenderPassword(pw); });
        }
      } catch (e) {}
    };
    fetchFlags();
  }, []);

  const fetchPropertyDetail = async (propertyId, summaryData) => {
    const requestId = ++detailRequestRef.current;
    const cover = summaryData?.first_photo_url || summaryData?.image_url || summaryData?.photo_url;
    setPropertyDetail(summaryData || null);
    setPropertyDetailImages(cover ? [cover] : []);
    setImagesLoading(!cover);
    try {
      const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-data/${propertyId}`, {});
      if (requestId !== detailRequestRef.current) return;
      if (response.ok) {
        const data = await response.json();
        const property = data.properties[0];
        setPropertyDetail({
          ...property,
          created_at: property.created_at || summaryData?.created_at,
          updated_at: property.updated_at || summaryData?.updated_at
        });
        if (property.listing_key) {
          fetchAllPropertyImages(property.listing_key, requestId, cover);
        }
      } else {
        setImagesLoading(false);
      }
    } catch (error) {
      console.error('Error fetching property details:', error);
      if (requestId === detailRequestRef.current) setImagesLoading(false);
    }
  };

  const fetchAllPropertyImages = async (listingKey, requestId, cover) => {
    try {
      const images = propertyMediaUrls(await loadPropertyMedia(listingKey));
      if (requestId !== detailRequestRef.current) return;
      if (images.length > 0) {
          const firstThree = images.slice(0, 3);
          setPropertyDetailImages(firstThree);
          setImagesLoading(false);
          rememberPropertyPhoto(listingKey, images[0]);
          if (images.length > 3) {
            setTimeout(() => {
              if (requestId === detailRequestRef.current) setPropertyDetailImages(images);
            }, 500);
          }
      } else {
        setPropertyDetailImages(cover ? [cover] : []);
        setImagesLoading(false);
      }
    } catch (error) {
      console.error('Error fetching all property images:', error);
      if (requestId === detailRequestRef.current) {
        setPropertyDetailImages(cover ? [cover] : []);
        setImagesLoading(false);
      }
    }
  };

  const closePropertyDetail = () => {
    detailRequestRef.current += 1;
    setPropertyDetail(null);
    setImagesLoading(false);
  };

  useEffect(() => {
    if (location.state?.properties) {
      setInitialProperties(location.state.properties);
      setProperties(location.state.properties);
      setListViewPage(0);
    }
  }, [location.state]);

  useEffect(() => {}, []);

  const user = location.state?.user;

  // Preload summary image files for the current and next page without another IDX request.
  useEffect(() => {
    if (!properties.length) return;
    const perPage = 9;
    const start = listViewPage * perPage;
    warmPropertyBatch(properties.slice(start, start + perPage), undefined, { media: false });
    warmPropertyBatch(properties.slice(start + perPage, start + perPage * 2));
  }, [properties, listViewPage]);

  const getTileCoords = (lat, lon, zoom) => {
    const x = Math.floor((lon + 180) / 360 * Math.pow(2, zoom));
    const y = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom));
    return { x, y };
  };

  useEffect(() => {
    if (expandedMap && mapRef.current) {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
      }

      const map = L.map(mapRef.current,{minZoom:1,maxZoom:19}).setView([expandedMap.lat, expandedMap.lon], 15);

      addIllustratedBasemap(map);

      const houseIcon = L.divIcon({
        html: '🏠',
        className: 'house-marker',
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });

      L.marker([expandedMap.lat, expandedMap.lon], { icon: houseIcon }).addTo(map);

      leafletMapRef.current = map;
      setPlacesExpandedMapInstance(map);
    }

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        setPlacesExpandedMapInstance(null);
      }
    };
  }, [expandedMap]);

  return (
    <div className={`property-list-view ${isDarkMode ? 'dark-mode' : ''}`}>
      <div style={{padding:'12px 20px'}}><RobustSearchControls filters={listSearch.filters} onChange={listSearch.setFilters} scope={{state:listSearch.filters.state || SITE_CONFIG.SEARCH_STATES || SITE_CONFIG.STATE, propertyType:listSearch.filters.propertyType || 'Residential'}} />
      {listSearch.loading && <p role="status">Loading matching homes…</p>}
      {listSearch.error && <p role="alert">{listSearch.error}</p>}</div>

      <div className="list-view-navigation">
        <button type="button" className="back-to-map-btn"
          onClick={() => {if(location.state?.isSavedHomes && location.state?.savedMap){sessionStorage.setItem('globeMapData',JSON.stringify(location.state.savedMap));navigate('/search?saved=true');}else{const params=searchQuery(listSearch.filters);params.set('view','map');navigate(`/search?${params}`);}}}>
          🗺 Map View
        </button>
        <button type="button" className="back-to-map-btn"
          onClick={() => {const params=searchQuery(listSearch.filters);params.set('view','filters');navigate(`/search?${params}`);}}>
          ← Search Filters
        </button>
      </div>

      <div className="list-view-container">
        <div className={`quick-filter-bar ${mobileFiltersOpen ? 'mobile-open' : 'mobile-collapsed'}`}>
          <div className="quick-filter-mobile-summary">
            <div className="quick-filter-mobile-count" aria-live="polite">
              {filtersLoading ? 'Searching homes...' : `${properties.length} ${properties.length === 1 ? 'home' : 'homes'}`}
            </div>
            <button
              type="button"
              className="quick-filter-mobile-toggle"
              onClick={() => setMobileFiltersOpen(open => !open)}
              aria-expanded={mobileFiltersOpen}
              aria-controls="mobile-quick-filters"
            >
              Filters
              {activeFilterCount > 0 && <span className="quick-filter-count-badge">{activeFilterCount}</span>}
              <span className="quick-filter-chevron" aria-hidden="true">{mobileFiltersOpen ? '▴' : '▾'}</span>
            </button>
          </div>

          {!mobileFiltersOpen && activeFilterChips.length > 0 && (
            <div className="quick-filter-chips" aria-label="Active filters">
              {activeFilterChips.map(filter => (
                <button
                  key={filter.key}
                  type="button"
                  className="quick-filter-chip"
                  onClick={() => updateQuickFilter(filter.key, '')}
                  aria-label={`Remove ${filter.label} filter`}
                >
                  {filter.label} <span aria-hidden="true">×</span>
                </button>
              ))}
            </div>
          )}

          <div id="mobile-quick-filters" className="quick-filter-panel">
            <div className="quick-filter-main">
            <label className="quick-filter-field quick-filter-city">
              <span>City or Area</span>
              <input
                type="text"
                value={quickFilters.city}
                onChange={(e) => updateQuickFilter('city', e.target.value)}
                placeholder="Delray Beach, Boca Raton, Boynton Beach..."
              />
            </label>
            <label className="quick-filter-field">
              <span>Min Price</span>
              <select value={quickFilters.minPrice} onChange={(e) => updateQuickFilter('minPrice', e.target.value)}>
                {priceOptions.map(option => <option key={option.value || 'min-any'} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <label className="quick-filter-field">
              <span>Max Price</span>
              <select value={quickFilters.maxPrice} onChange={(e) => updateQuickFilter('maxPrice', e.target.value)}>
                {priceOptions.map(option => <option key={option.value || 'max-any'} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <label className="quick-filter-field">
              <span>Beds</span>
              <select value={quickFilters.minBedrooms} onChange={(e) => updateQuickFilter('minBedrooms', e.target.value)}>
                {bedroomOptions.map(option => <option key={option.value || 'beds-any'} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            {isFilterActive && (
              <button type="button" className="quick-filter-clear" onClick={clearQuickFilters}>
                Clear
              </button>
            )}
            </div>
            <button
              type="button"
              className="quick-filter-mobile-done"
              onClick={() => setMobileFiltersOpen(false)}
              disabled={filtersLoading}
            >
              {filtersLoading ? 'Searching homes...' : `Show ${properties.length} ${properties.length === 1 ? 'home' : 'homes'}`}
            </button>
          </div>
          <div className="quick-filter-status quick-filter-status-desktop" aria-live="polite">
            {filtersLoading ? 'Searching homes...' : `${properties.length} ${properties.length === 1 ? 'home' : 'homes'}`}
          </div>
        </div>

        {filtersError && (
          <div className="list-view-state list-view-error">{filtersError}</div>
        )}

        {!filtersLoading && !filtersError && properties.length === 0 && (
          <div className="list-view-state">
            No homes match these filters.
          </div>
        )}

        {properties.slice(listViewPage * 9, (listViewPage + 1) * 9).map((property, index) => {
          return (
            <div
              key={index}
              className="list-view-card"
              onClick={() => fetchPropertyDetail(property.id, property)}
            >
              {isAdmin && (
                <div
                  className="list-view-select"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedListings(prev => {
                      const next = new Set(prev);
                      if (next.has(property.listing_key)) next.delete(property.listing_key);
                      else if (next.size < 3) next.add(property.listing_key);
                      return next;
                    });
                  }}
                >
                  <input type="checkbox" checked={selectedListings.has(property.listing_key)} readOnly />
                </div>
              )}
              <div className="list-view-hero">
                <PropertyThumbnail
                  listingKey={property.listing_key}
                  src={property.first_photo_url || property.image_url || property.photo_url}
                  alt={`Property at ${property.address || property.street_address}`}
                  className="list-view-image"
                />
                <div className="list-view-overlay">
                  <div className="list-view-price">
                    ${property.current_price?.toLocaleString() || property.price}
                  </div>
                  <div className="list-view-address">
                    {formatPropertyAddress(property)}
                  </div>
                  <div className="list-view-meta">
                    {property.bedrooms_total && <span>🛏️ {property.bedrooms_total} bed</span>}
                    {property.property_type && <span>{property.property_type}</span>}
                  </div>
                </div>
                {property.latitude && property.longitude && (
                  <div
                    className="list-view-map"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedMap({lat: property.latitude, lon: property.longitude, address: property.address});
                    }}
                  >
                    <img
                      src={`https://tile.openstreetmap.org/15/${getTileCoords(property.latitude, property.longitude, 15).x}/${getTileCoords(property.latitude, property.longitude, 15).y}.png`}
                      alt={`Map location for ${property.address || property.street_address}`}
                    />
                    <div className="list-view-house-icon">🏠</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <div className="pagination-controls">
          {listSearch.restartSearch && <button onClick={()=>listSearch.restart()}>Restart search</button>}
          {properties.length > 0 && listViewPage > 0 && (
            <button
              disabled={listSearch.loading} onClick={() => setListViewPage(listViewPage - 1)}
              className="pagination-btn"
            >
              ← Previous
            </button>
          )}
          {properties.length > 0 && (
            <span className="pagination-info">
              Page {listViewPage + 1}
            </span>
          )}
          {properties.length > 0 && (listSearch.hasMore || (listViewPage + 1) * 9 < properties.length) && (
            <button
              disabled={listSearch.loading} onClick={() => listSearch.nextPage(listViewPage)}
              className="pagination-btn"
            >
              Next →
            </button>
          )}
        </div>
        <div className="mobile-map-disclosure" style={{gridColumn: '1 / -1', background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', borderRadius: '8px'}}>
          {SITE_CONFIG.MLS.LOGO_URL && <img src={SITE_CONFIG.MLS.LOGO_URL} alt={SITE_CONFIG.MLS.SHORT_NAME} style={{height: '20px', objectFit: 'contain'}} />}
          <span style={{fontSize: '10px', color: '#ccc', flex: 1}}>{SITE_CONFIG.MLS.DISCLAIMER} | Brokerage License: {SITE_CONFIG.AGENT.BROKERAGE_LICENSE} | Agent License: {SITE_CONFIG.AGENT.LICENSE}</span>
        </div>
      </div>

      {isAdmin && selectedListings.size > 0 && (
        <div className="bulk-email-bar">
          <span>{selectedListings.size}/3 selected</span>
          <button onClick={() => { setBulkEmailSubject('Featured Properties'); setBulkEmailPreview(''); setShowBulkEmailModal(true); }}>
            ✉️ Email Selected
          </button>
          <button onClick={() => setSelectedListings(new Set())}>Clear</button>
        </div>
      )}

      {showBulkEmailModal && (
        <div className="modal-overlay" onClick={() => setShowBulkEmailModal(false)}>
          <div className="email-send-modal" style={{position: 'relative', margin: 'auto', top: '20vh'}} onClick={(e) => e.stopPropagation()}>
            <div className="email-send-modal-content">
              <button className="email-send-modal-close" onClick={() => setShowBulkEmailModal(false)}>×</button>
              <h3>Email {selectedListings.size} Properties</h3>
              {bulkEmailPreview === 'sent' ? (
                <div style={{textAlign: 'center', padding: '30px 10px'}}>
                  <div style={{fontSize: '40px', marginBottom: '10px'}}>✅</div>
                  <p style={{color: '#28a745', fontWeight: 600, margin: 0}}>Email sent successfully!</p>
                </div>
              ) : !bulkEmailPreview ? (
                <div className="email-send-form">
                  <input type="email" placeholder="Recipient email *" value={bulkEmailRecipient} onChange={(e) => setBulkEmailRecipient(e.target.value)} />
                  <input type="text" placeholder="Customer name (optional)" value={bulkEmailCustomerName} onChange={(e) => setBulkEmailCustomerName(e.target.value)} />
                  <input type="text" placeholder="Subject *" value={bulkEmailSubject} onChange={(e) => setBulkEmailSubject(e.target.value)} />
                  <button
                    disabled={!bulkEmailRecipient.trim() || !bulkEmailSubject.trim() || bulkEmailGenerating}
                    onClick={async () => {
                      setBulkEmailGenerating(true);
                      try {
                        const res = await fetch('https://tophamtechbe.com/api/treas/comm/property-search-preview?format=html', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-API-Key': 'treas-api-key-2025' },
                          body: JSON.stringify({
                            customerName: bulkEmailCustomerName || undefined,
                            agentName: SITE_CONFIG.AGENT.NAME,
                            siteId: SITE_CONFIG.SITE_ID,
                            listingBaseUrl: `https://www.${SITE_CONFIG.BASE_URL}`,
                            searchFilters: { listingKey: [...selectedListings].join(',') }
                          })
                        });
                        const data = await res.json();
                        if (data.html) setBulkEmailPreview(data.html);
                        else alert(data.message || 'No preview generated');
                      } catch (err) {
                        alert('Error generating preview');
                      }
                      setBulkEmailGenerating(false);
                    }}
                  >
                    {bulkEmailGenerating ? 'Generating...' : 'Preview Email'}
                  </button>
                </div>
              ) : (
                <div className="email-send-preview">
                  <iframe title="Email Preview" srcDoc={bulkEmailPreview} style={{width: '100%', height: '300px', border: '1px solid #ddd', borderRadius: '4px'}} />
                  <div className="email-send-actions">
                    <button onClick={() => setBulkEmailPreview('')}>← Back</button>
                    <button
                      disabled={bulkEmailSending}
                      onClick={async () => {
                        setBulkEmailSending(true);
                        try {
                          const formData = new FormData();
                          formData.append('emailaddress', bulkEmailRecipient);
                          formData.append('title', bulkEmailSubject);
                          formData.append('body', bulkEmailPreview);
                          if (senderEmail) formData.append('email', senderEmail);
                          if (senderPassword) formData.append('emailPassword', senderPassword);
                          const res = await sendEmailRequest('https://tophamtechbe.com/api/treas/comm/send-email', {
                            method: 'POST',
                            headers: { 'X-API-Key': 'treas-api-key-2025' },
                            body: formData
                          });
                          if (res.ok) {
                            setBulkEmailPreview('sent');
                            setTimeout(() => { setShowBulkEmailModal(false); setSelectedListings(new Set()); }, 2000);
                          } else alert('Failed to send email');
                        } catch (err) {
                          alert('Error sending email');
                        }
                        setBulkEmailSending(false);
                      }}
                    >
                      {bulkEmailSending ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <PropertyDetailModal
        propertyDetail={propertyDetail}
        propertyDetailImages={propertyDetailImages}
        imagesLoading={imagesLoading}
        onClose={closePropertyDetail}
        user={user}
        isAdmin={isAdmin}
        likedProperties={likedProperties}
        setLikedProperties={setLikedProperties}
        senderEmail={senderEmail}
        senderPassword={senderPassword}
      />
      {expandedMap && (
        <div className="modal-overlay" onClick={() => setExpandedMap(null)}>
          <div className="expanded-map" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setExpandedMap(null)}>×</button>
            <div ref={mapRef} className="leaflet-map"></div>
              <MapPlaceLayers map={placesExpandedMapInstance} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyListView;
